import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import gsap from 'gsap';

const WebGLDisk = ({ size = 150, responsiveSize = null }) => {
  const diskContainerRef = useRef(null);
  const sceneRef = useRef(null);
  const diskRef = useRef(null);
  const mouseXRef = useRef(0);
  const mouseYRef = useRef(0);
  const timeRef = useRef(0);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [currentSize, setCurrentSize] = useState(size);

  const calculateResponsiveSize = () => {
    if (responsiveSize) {
      // Parse the responsive size calculation (e.g., "calc((100vw / 12.5) - 0.45rem)")
      const vwMatch = responsiveSize.match(/100vw\s*\/\s*(\d+(?:\.\d+)?)/);
      const remMatch = responsiveSize.match(/(\d+(?:\.\d+)?)rem/);
      
      if (vwMatch && remMatch) {
        const vwDivisor = parseFloat(vwMatch[1]);
        const remValue = parseFloat(remMatch[1]);
        const calculatedSize = (window.innerWidth / vwDivisor) - (remValue * 16); // Convert rem to px
        return Math.max(calculatedSize, 50); // Minimum size of 50px
      }
    }
    return size;
  };

  const updateSize = () => {
    const newSize = calculateResponsiveSize();
    setCurrentSize(newSize);
  };

  const initDisk = () => {
    if (!diskContainerRef.current) return;

    // Setup scene
    sceneRef.current = new THREE.Scene();
    sceneRef.current.fog = new THREE.Fog(0xffffff, 5, 11);
    sceneRef.current.background = new THREE.Color().setHex(0xffffff);
    
    cameraRef.current = new THREE.OrthographicCamera(-1, 1, 1, -1, 1, 1000);
    cameraRef.current.position.z = 6;
    
    rendererRef.current = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true 
    });
    rendererRef.current.setSize(currentSize, currentSize);
    rendererRef.current.shadowMap.enabled = false;
    diskContainerRef.current.appendChild(rendererRef.current.domElement);

    // Create disk geometry - same as Home.js
    const diskSize = 2;
    const thickness = 0.1;
    const geometry = new THREE.SphereGeometry(diskSize/2, 32, 32);
    const material = new THREE.MeshPhongMaterial({ 
      color: 0xff0000,
      side: THREE.FrontSide,
      flatShading: true,
      shininess: 10,
      specular: 0xff2222,
      transparent: true,
      opacity: 0.9
    });

    diskRef.current = new THREE.Mesh(geometry, material);
    diskRef.current.originalPosition = {
      x: 0,
      y: 0,
      z: 0
    };
    
    // Same animation in as Home.js
    diskRef.current.scale.x = 0;
    diskRef.current.scale.y = 0;
    diskRef.current.scale.z = 0;
    
    gsap.to(diskRef.current.scale, {
      x: 1,
      y: 1,
      z: thickness,
      duration: 2,
      delay: 0.2,
      ease: "power1.inOut"
    });
    
    sceneRef.current.add(diskRef.current);

    // Add lights with same intensity as Home.js
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.4);
    sceneRef.current.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(0, 0, 1);
    sceneRef.current.add(directionalLight);

    const backLight = new THREE.DirectionalLight(0xffffff, 0.6);
    backLight.position.set(1, -0.5, -1);
    sceneRef.current.add(backLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.6);
    fillLight.position.set(-1, 0.5, -1);
    sceneRef.current.add(fillLight);
  };

  const onMouseMove = (event) => {
    mouseXRef.current = (event.clientX / window.innerWidth) * 20 - 1;
    mouseYRef.current = -(event.clientY / window.innerHeight) * 20 + 1;
  };

  const animateDisk = () => {
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current || !diskRef.current) return;

    timeRef.current += 0.0025;

    // Same cursor response as Home.js but without noise
    const deltaX = mouseXRef.current * 15 - diskRef.current.originalPosition.x;
    const deltaY = mouseYRef.current * 15 - diskRef.current.originalPosition.y;

    const maxRotation = 0.75;
    const targetY = (deltaX / 30) * maxRotation;
    const targetX = (deltaY / 30) * maxRotation;

    diskRef.current.rotation.y += (targetY - diskRef.current.rotation.y) * 0.05;
    diskRef.current.rotation.x += (targetX - diskRef.current.rotation.x) * 0.05;

    rendererRef.current.render(sceneRef.current, cameraRef.current);
    animationFrameRef.current = requestAnimationFrame(animateDisk);
  };

  const cleanupDisk = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    if (rendererRef.current && diskContainerRef.current) {
      diskContainerRef.current.removeChild(rendererRef.current.domElement);
      rendererRef.current.dispose();
    }

    if (diskRef.current) {
      diskRef.current.geometry.dispose();
      diskRef.current.material.dispose();
    }
  };

  useEffect(() => {
    updateSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responsiveSize]);

  useEffect(() => {
    initDisk();
    animateDisk();

    window.addEventListener('mousemove', onMouseMove);

    const handleResize = () => {
      if (responsiveSize) {
        updateSize();
        if (rendererRef.current) {
          rendererRef.current.setSize(currentSize, currentSize);
        }
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', handleResize);
      cleanupDisk();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSize]);

  return <div ref={diskContainerRef} style={{ width: currentSize, height: currentSize }} />;
};

export default WebGLDisk; 